import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Row,
  Col,
  ProgressBar,
  OverlayTrigger,
  Popover
} from 'react-bootstrap';
import { fetchHVACallScorecard } from 'services/hvaqa.service';
import Button from 'components/base/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons';
import ScorecardReviewModal from 'pages/hvaqualityassurance/ScorecardReviewModal';

interface CallScorecardProps {
  callid: string;
  campaignid: number;
}

// Define una interfaz para el tipo de pregunta
interface Question {
  id: number;
  title: string;
  scoreValue: number;
  progressValue: number;
  answerText: string;
  qaAgentComment: string | null;
  qaAgentName: string;
}

// Define una interfaz para el tipo de scorecard
interface Scorecard {
  id: number;
  name: string;
  overallScore: string | number;
  questions: Question[];
}

export const CallScoreCard: React.FC<CallScorecardProps> = ({
  callid,
  campaignid
}) => {
  const [scorecards, setScorecards] = useState<Scorecard[]>([]); // Estado para las scorecards y sus datos
  const [showReviewModal, setShowReviewModal] = useState<{
    [key: number]: boolean;
  }>({});

  const fetchScorecardsData = useCallback(async () => {
    try {
      const data = await fetchHVACallScorecard({
        callid,
        campaignid
      });

      // Procesa cada scorecard en los datos
      // eslint-disable-next-line
      const formattedScorecards: Scorecard[] = data.map((scorecard: any) => {
        const { hvascorecardquestions, hvascorecardcalls } = scorecard;

        // Extrae las respuestas del answertext para cada scorecard
        let answersMap: { [key: string]: string } = {};
        if (hvascorecardcalls.length > 0) {
          const answertextObj = JSON.parse(hvascorecardcalls[0]?.answertext);
          // eslint-disable-next-line
          answersMap = answertextObj.response.reduce((acc: any, item: any) => {
            const answerContent = item.answer.split('\n').slice(1).join('\n');
            acc[item.question] = answerContent;
            return acc;
          }, {});
        }

        // Calcula la puntuación general para cada scorecard
        const overallValues =
          hvascorecardcalls[0]?.hvascorecardcallvalues || [];
        const overallScoreValue =
          overallValues[0]?.qascorevalue !== null &&
          overallValues[0]?.qascorevalue !== undefined
            ? overallValues[0]?.qascorevalue
            : overallValues[0]?.iascorevalue;

        // Si no hay valores disponibles, muestra 'n/a'; de lo contrario, muestra el valor calculado
        const overallScore =
          overallScoreValue !== null && overallScoreValue !== undefined
            ? overallScoreValue
            : 'n/a';

        // Mapea los datos de las preguntas para el renderizado dinámico
        // eslint-disable-next-line
        const formattedQuestions: Question[] = hvascorecardquestions.map((question: any) => {
            const id = question.hvascorecardcallquestionvalue?.questionid;
            const scoreValue =
              question.hvascorecardcallquestionvalue?.qascorevalue ??
              question.hvascorecardcallquestionvalue?.iascorevalue ??
              0; // Si no hay valores, usa 0 como predeterminado

            // Asigna la respuesta correspondiente basada en el title/questiontext
            const answer = answersMap[question.questiontext] || '';

            // Extrae comentarios de agente y nombre del agente
            const qaAgentComment =
              question.hvascorecardcallquestionvalue?.qaagentcoment;
            const account = question.hvascorecardcallquestionvalue?.account;
            const qaAgentName = account
              ? `${account.name} ${account.lastname}`
              : '';

            return {
              id: id,
              title: question.questiontitle,
              scoreValue: scoreValue,
              progressValue: scoreValue * 10, // Multiplica por 10 para el valor del ProgressBar
              answerText: answer,
              qaAgentComment: qaAgentComment,
              qaAgentName: qaAgentName
            };
          }
        );

        return {
          id: scorecard.id,
          name: scorecard.name,
          overallScore: overallScore,
          questions: formattedQuestions
        };
      });

      setScorecards(formattedScorecards);
    } catch (error) {
      console.error('Error fetching scorecards:', error);
    }
  }, [callid, campaignid]);

  useEffect(() => {
    fetchScorecardsData();
  }, [fetchScorecardsData]);

  // Determinar la clase del Card basándose en el overallScore
  const getCardVariant = (overallScore: string | number) => {
    if (overallScore === 'n/a') return 'secondary-light'; // Default color if no score
    if (typeof overallScore === 'number') {
      if (overallScore < 6) return 'primary';
      if (overallScore >= 6 && overallScore < 8) return 'warning';
      if (overallScore >= 8) return 'success';
    }
    return 'secondary'; // Fallback color
  };

  const handleReviewClick = (scorecardIndex: number) => {
    setShowReviewModal(prev => ({ ...prev, [scorecardIndex]: true }));
  };

  const handleUpdate = () => {
    fetchScorecardsData(); // Refetch data when modal closes
  };

  return (
    <>
      {scorecards.length > 0 && (
        <>
          <h5 className="pt-3 mb-3">Scorecards for this call</h5>
          {scorecards.map((scorecard, scorecardIndex) => (
            <div key={scorecardIndex} className="mb-6">
              <div className="d-flex justify-content-between align-items-center">
                {/* Nombre de la Scorecard */}
                <p className="fs-9 fw-bold text-body-emphasis mb-0">
                  {scorecard.name}
                </p>
                {scorecard.overallScore !== 'n/a' && (
                  <Button
                    variant="link"
                    className="p-0 text-decoration-none"
                    onClick={() => handleReviewClick(scorecardIndex)}
                  >
                    Review/score this call
                    <FontAwesomeIcon
                      icon={faChevronRight}
                      className="px-1 pe-0"
                    />
                  </Button>
                )}
              </div>
              <Row>
                <Col xs={2} sm={2} md={2} xxl={2}>
                  <OverlayTrigger
                    trigger="hover"
                    placement="top"
                    overlay={
                      <Popover id="popover" className="shadow-lg">
                        <Popover.Body>
                          <div style={{ width: '200px' }}>
                            <p className="lh-sm fs-9">
                              Card background color based on score value:
                            </p>
                            <ProgressBar
                              style={{ height: '15px', width: '100' }}
                            >
                              <ProgressBar
                                style={{
                                  backgroundColor: '#c00000'
                                }}
                                now={56}
                                label="0-6"
                                className="lh-1 text-secondary-subtle fw-bold"
                              />
                              <ProgressBar
                                variant="warning"
                                now={22}
                                label="6-8"
                                className="lh-1 text-secondary-subtle fw-bold"
                              />
                              <ProgressBar
                                variant="success"
                                now={22}
                                label="8-10"
                                className="lh-1 text-secondary-subtle fw-bold"
                              />
                            </ProgressBar>
                          </div>
                        </Popover.Body>
                      </Popover>
                    }
                  >
                    {/* Tarjeta de puntuación total con color dinámico */}
                    <Card
                      className={`bg-${getCardVariant(
                        scorecard.overallScore
                      )} text-center mt-2`}
                    >
                      <span className="fs-9 p-0 pt-2 text-secondary-subtle">
                        Score
                      </span>
                      <h3 className="text-secondary-subtle pb-2">
                        {scorecard.overallScore}
                      </h3>
                    </Card>
                  </OverlayTrigger>
                </Col>
                <Col>
                  {scorecard.questions.map(
                    (question: Question, index: number) => (
                      <React.Fragment key={index}>
                        {/* Bloque de pregunta dinámico */}
                        <Row className="d-flex ">
                          <Col>
                            <span className="fs-9 p-0">{question.title}</span>
                          </Col>
                          <Col className="fs-9 fw-bold text-end text-emphasis pe-4 pt-1">
                            {question.scoreValue !== null
                              ? question.scoreValue
                              : 0}
                          </Col>
                        </Row>
                        {/* Siempre renderiza ProgressBar, usando 0 como valor predeterminado si no hay valores */}
                        {question.scoreValue > 0 ? (
                          <OverlayTrigger
                            key={'Q-' + index}
                            trigger="hover"
                            placement="top"
                            overlay={
                              <Popover
                                id="popover"
                                className="shadow-lg lh-sm fs-9"
                              >
                                <Popover.Body>
                                  {question.qaAgentComment ? (
                                    <div>
                                      <p className="fw-bold mb-0 pb-0 mb-0">
                                        QA comment
                                        <br />
                                        <span className="fw-normal fs-10 pt-0 pb-0 mt-0 mb-0 text-primary">
                                          {question.qaAgentName
                                            ? `(by ${question.qaAgentName})`
                                            : ''}
                                        </span>
                                      </p>
                                      <p className="fw-normal pt-0 pb-0 mt-1 mb-3">
                                        {question.qaAgentComment}
                                      </p>
                                    </div>
                                  ) : null}
                                  <p className="fw-bold mb-0 pb-0 mb-1">
                                    AI comment
                                  </p>
                                  <p className="fw-normal pt-0 pb-0 mb-0">
                                    {question.answerText ||
                                      'No comment available'}
                                  </p>
                                </Popover.Body>
                              </Popover>
                            }
                          >
                            <ProgressBar
                              variant="primary-lighter"
                              now={question.progressValue}
                              className="mb-0 p-0 fs-10 fw-bold"
                              style={{ height: '11px' }}
                            />
                          </OverlayTrigger>
                        ) : (
                          <ProgressBar
                            variant="primary-lighter"
                            now={question.progressValue}
                            className="mb-0 p-0 fs-10 fw-bold"
                            style={{ height: '11px' }}
                          />
                        )}
                      </React.Fragment>
                    )
                  )}
                </Col>
              </Row>
              <ScorecardReviewModal
                show={showReviewModal[scorecardIndex] || false}
                // eslint-disable-next-line
                onHide={() => setShowReviewModal(prev => ({...prev, [scorecardIndex]: false}))}
                questions={scorecard.questions}
                overallScore={scorecard.overallScore}
                callid={callid}
                scorecardId={scorecard.id}
                scorecardName={scorecard.name}
                onUpdate={handleUpdate}
              />
            </div>
          ))}
        </>
      )}
    </>
  );
};
